// rraFormClient — PUBLIC (no-auth) client for the two RRA tokenized forms
// (docs/design/rra-workflow.md phase 3). Never attaches a Bearer token: the
// token in the path is the only credential (like intakeClient /
// verificationClient). Mirrors the wire shapes in
// apps/api/src/routes/rraForm.ts + rraVerifyForm.ts.

// `import.meta.env` exists only under Vite; the optional chain lets the offline page tests
// (npx tsx --test) load this module (the AuthContext precedent). The build still replaces it statically.
const API_BASE = import.meta.env?.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// Requester form (/rra/:token)
// ---------------------------------------------------------------------------

export type RraAnimalType = 'dog' | 'cat' | 'other';

export interface RraFormAnimal {
  name?: string;
  type?: RraAnimalType;
  otherDescription?: string;
  breed?: string;
  weightLbs?: number;
  age?: number;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  licenseNumber?: string;
}

/** WHAT the requester asks us to change (B3). `waiveAnimalCharges` is server-derived (= allowAnimal) on a
 *  v2.1 item, so it only rides the wire for an older item; nothing in the SPA sets it. */
export interface RraRequestStatement {
  allowAnimal: boolean;
  waiveAnimalCharges?: boolean;
  /** A change to another rule, or to how you do things (<=300). */
  otherRule?: string;
  /** What physical change to the home or building (<=300). */
  physicalChange?: string;
}

/** A1/C1: exactly what the form shows, recomputed live by the server from the recorded gate. */
export interface RraFormShape {
  /** "Your disability" (B5) is shown. */
  askDisability: boolean;
  /** Open items whose need is in the gap (help box + need affirmation; the two questions on a service animal). */
  needItemIds: string[];
  /** "Confirming your request" (B8) is shown. */
  askConfirmation: boolean;
  /** Neither prong in the gap: confirm-and-send (B9). */
  confirmOnly: boolean;
}

export interface RraFormItemView {
  itemId: string;
  scope: 'esa_animal' | 'service_animal' | 'other_accommodation' | 'modification';
  /** esa_animal | service_animal. */
  isAnimal: boolean;
  /** Need is in the gap for this item. */
  inNeedGap: boolean;
  /** RRA v2.2: 1-based position among ALL the case's requests, in creation order — so a form reopened
   *  for only the newest request still says "Request 3", matching the request copy. Absent on an
   *  older server; the form then numbers by its own order. */
  ordinal?: number;
  /** LEGACY items only (a case opened before RRA v2.2 carried staff-typed animal details). New
   *  requests never carry one, and the form no longer asks for animal facts. */
  animal?: RraFormAnimal & { id?: string };
  /** The staff's one-line statement of the request — a `modification` item prefills its physical change from it. */
  description?: string;
  request?: RraRequestStatement;
  /** Prefill only. */
  howChangeHelps?: string;
  /** Prefill, service_animal items only. */
  serviceAnimalQuestions?: {
    requiredBecauseOfDisability?: boolean;
    supportProvided?: string;
  };
}

export interface RraFormVerifier {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export type RraFormState =
  | 'fillable'
  | 'submitted'
  | 'permission'
  | 'permission_done'
  | 'closed'
  | 'unavailable';

export interface RraFormView {
  state: RraFormState;
  requesterName: string;
  items: RraFormItemView[];
  shape: RraFormShape;
  /** The stored subject; null until the requester (or staff) chose one. */
  subject: { kind: 'self' } | { kind: 'household_member'; name: string; minor: boolean } | null;
  authorization: { granted: boolean; withdrawn: boolean };
  uploadedLetters: Array<{ filename: string; uploadedAt: string }>;
  /** The full request copy is on file (a boolean — never the pointer). */
  signedRequest: boolean;
  /** `signed`: the requester signed on the form (false on confirm-and-send and on a staff fill-in). */
  submitted?: { confirmOnly: boolean; signed: boolean; nextStep: string; download: boolean };
  permission?: {
    signer: 'requester' | 'subject';
    signerIsGuardian: boolean;
    subjectName: string;
    requesterName: string;
    proposedVerifiers: RraFormVerifier[];
    needIsAnimal: boolean;
    summary: string;
    /** When the ask was sent — only a letter uploaded after it answers it. */
    askedAt: string;
  };
}

export interface RraFormItemSubmission {
  itemId: string;
  request: { allowAnimal: boolean; otherRule?: string; physicalChange?: string };
  /** B4: the one optional help box (<=300). Only sent for an item the shape asks it of. */
  howChangeHelps?: string;
  /** Service-animal items with need in the gap only. */
  requiredBecauseOfDisability?: boolean;
  supportProvided?: string;
}

export interface RraFormSubmissionBody {
  formVersion: 'v2.1';
  subject: { kind: 'self' } | { kind: 'household_member'; name: string; minor: boolean };
  items: RraFormItemSubmission[];
  affirmation: { hasDisability?: true; needsBecauseOfDisability?: true };
  authMethod?: 'contacts' | 'letter' | 'later';
  verifiers?: RraFormVerifier[];
  /** An adult household member: their own typed name (they are here), or an email for their own link. */
  subjectPermission?: { via: 'typed'; signedName: string } | { via: 'link'; email: string };
  signedName?: string;
  /** confirm-and-send (B9): nothing attested, nothing signed. */
  confirmed?: true;
}

/** The permission page's body (A6 / B2). */
export type RraPermissionBody =
  | { method: 'contacts'; verifiers: RraFormVerifier[]; signedName: string }
  | { method: 'letter' };

export class RraFormApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`RRA form API error ${status}`);
    this.name = 'RraFormApiError';
  }

  /** A friendly, human-readable message for a banner. Prefers the server's
   *  single `message`, then the joined per-field `messages`, then the legacy
   *  `error` string — never the bare "Bad request" if anything better exists. */
  get errorMessage(): string | null {
    const b = this.body as
      | { error?: string; message?: string; messages?: Record<string, string> }
      | null;
    if (!b) return null;
    if (b.message) return b.message;
    if (b.messages && Object.keys(b.messages).length > 0) {
      return Object.values(b.messages).join(' ');
    }
    return b.error ?? null;
  }

  /** path (dot-joined) → friendly sentence, for per-field rendering. */
  get fieldMessages(): Record<string, string> {
    return (this.body as { messages?: Record<string, string> } | null)?.messages ?? {};
  }
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new RraFormApiError(res.status, body);
  }
  return (await res.json()) as T;
}

export function getRraForm(token: string): Promise<RraFormView> {
  return publicRequest<RraFormView>(`/api/rra-form/${encodeURIComponent(token)}`);
}

export function submitRraForm(
  token: string,
  body: RraFormSubmissionBody,
): Promise<{ ok: boolean; view: RraFormView }> {
  return publicRequest(`/api/rra-form/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Upload an existing letter (single PDF, multipart). QUARANTINED server-side:
 *  the file is stored; only a pointer + filename touch the case record. */
export function uploadRraLetter(
  token: string,
  file: File,
): Promise<{ ok: boolean; view: RraFormView }> {
  const form = new FormData();
  form.append('file', file);
  return publicRequest(`/api/rra-form/${encodeURIComponent(token)}/upload-letter`, {
    method: 'POST',
    body: form,
  });
}

/** The permission page (A6 / B2): name someone, or send a letter already uploaded. */
export function submitRraPermission(
  token: string,
  body: RraPermissionBody,
): Promise<{ ok: boolean; view: RraFormView }> {
  return publicRequest(`/api/rra-form/${encodeURIComponent(token)}/permission`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Absolute URL of the requester's own copy (B11) — a plain `<a href download>` target. */
export function rraRequestPdfUrl(token: string): string {
  return `${API_BASE}/api/rra-form/${encodeURIComponent(token)}/request.pdf`;
}

// ---------------------------------------------------------------------------
// Verifier form (/rra-verify/:token) — RRA v2.1 (W3, E2/E3)
// Wire shapes mirror apps/api/src/routes/rraVerifyForm.ts. PUBLIC, token-gated.
// The call-sheet client (/rra-call/:token) moved to lib/rraCallClient.ts: it is
// no longer public (staff sign-in + RRA caller roster).
// ---------------------------------------------------------------------------

/** One prong's answer: 'cant_say' is "I can't say" on the web form and records no evidence. */
export type RraProngAnswer = 'yes' | 'no' | 'cant_say';

/** The B5 definition block (rraCopy.disabilityDefinition with the subject's name). */
export interface RraVerifyDefinition {
  intro: string;
  bullets: [string, string, string];
  tail: string;
}

export interface RraVerifyFormView {
  state: 'fillable' | 'submitted' | 'closed' | 'unavailable';
  /** The person with the disability — never the requester unless they are the same. */
  subjectName: string;
  /** rraCopy.verifyRequestLine(subjectName, summary). */
  requestLine: string;
  askDisability: boolean;
  askNeed: boolean;
  /** Present when askDisability. */
  definition?: RraVerifyDefinition;
  /** rraCopy.verifierNeedObject — "their animal at home" / "the change they asked for". */
  needObject: string;
  /** The requester's own words for a non-animal change, shown above the need question. */
  changeTexts: string[];
  /** A live permission names this verifier (the copy link is shown). */
  copyAvailable: boolean;
}

export interface RraVerifySubmissionBody {
  positionToKnow: 'yes' | 'no';
  disability?: RraProngAnswer;
  need?: RraProngAnswer;
  respondent: { name: string; email?: string };
}

export {
  BLANK_VERIFY_DRAFT,
  buildVerifySubmission,
  verifyDraftProblem,
  type RraVerifyFormDraft,
} from './rraVerifyDraft';

export function getRraVerifyForm(token: string): Promise<RraVerifyFormView> {
  return publicRequest<RraVerifyFormView>(`/api/rra-verify/${encodeURIComponent(token)}`);
}

export function submitRraVerifyForm(
  token: string,
  body: RraVerifySubmissionBody,
): Promise<{ ok: boolean; view: RraVerifyFormView }> {
  return publicRequest(`/api/rra-verify/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Absolute URL of THIS verifier's copy of the signed request (E3; opens in a new tab). */
export function rraVerifyCopyUrl(token: string): string {
  return `${API_BASE}/api/rra-verify/${encodeURIComponent(token)}/copy.pdf`;
}
